// Pure event-shaping helpers for `sentry.server.config.ts`'s `beforeSend`.
// They live here rather than in the config because importing that module runs
// `Sentry.init()` as a side effect, which makes it untestable.

import type { ErrorEvent } from "@sentry/nextjs"

/**
 * Patterns we deliberately drop server-side before sending to Sentry.
 *
 * Each pattern targets a console.error that the runtime catches and recovers
 * from — the page still renders successfully — so the event has no actionable
 * signal but burns quota. We drop ONLY when the event came through
 * captureConsoleIntegration.
 *
 * Verified non-impacting before adding here:
 * - Stale Turbopack chunks: client triggers a hard reload via Next's
 *   ChunkLoadError handling; transactions on affected routes show 0 5xx.
 */
const DROPPED_CONSOLE_MESSAGE_PATTERNS: RegExp[] = [
  // Turbopack stale-chunk after a deploy — Next handles this with a hard reload.
  /Module \d+ was instantiated because it was required from module \d+, but the module factory is not available/,
]

/**
 * `captureConsoleIntegration` renamed its mechanism between SDK majors: v8 set
 * `console`, v9+ sets `auto.core.capture_console` (see
 * `@sentry/core/build/*\/integrations/captureconsole.js`). The check only knew
 * the v8 name, so from the v10 bump until this fix `isDroppedConsoleNoise`
 * returned false for every event and the Turbopack drop above was silently
 * dead. Accept both names so an SDK bump can't disarm the filter again.
 */
const CONSOLE_MECHANISMS = new Set(["console", "auto.core.capture_console"])

/**
 * `throwOnCmsFailure` (lib/cms-server.ts) throws when a CMS fetch failed, so
 * Next never caches a "not found" shell built from an empty response. That
 * throw is correct and stays — but it is reported in EVERY environment, while
 * the diagnostic report it points at ("CMS fetch failed: <tag>", carrying the
 * path and HTTP status) is production-only: #548 deliberately suppressed CMS
 * warnings outside production, and the throw landed two days later without the
 * matching gate. Outside production that leaves a pointer with no target — an
 * error reading "See Sentry for the underlying error" when no such event was
 * ever sent. That inversion is the whole of MARKETING-SITE-101: 1278 of its
 * 1363 events are preview deploys.
 */
// Positional rather than named groups: the repo's tsconfig target predates
// ES2018, so `(?<name>…)` is a compile error here.
const CMS_FETCH_WRAPPER =
  /^CMS fetch failed for ([^/]+)\/(.*?) — refusing to cache/

function consoleMessage(event: ErrorEvent): string | null {
  const mechanism = event.exception?.values?.[0]?.mechanism?.type
  if (!mechanism || !CONSOLE_MECHANISMS.has(mechanism)) return null
  return event.exception?.values?.[0]?.value ?? ""
}

/**
 * Drop the wrapper wherever its underlying report is suppressed, so the two
 * agree. Production still reports it — there a CMS failure is real.
 */
export function isDroppedConsoleNoise(
  event: ErrorEvent,
  vercelEnv: string | undefined
): boolean {
  const message = consoleMessage(event)
  if (message === null) return false
  if (vercelEnv !== "production" && CMS_FETCH_WRAPPER.test(message)) return true
  return DROPPED_CONSOLE_MESSAGE_PATTERNS.some((re) => re.test(message))
}

/**
 * The wrapper embeds the slug in its message, so Sentry fingerprints one issue
 * per slug — a single CMS outage fans out into `biomarkers/uric-acid`,
 * `authors/nick-lenten`, `faq-categories/our-scans-and-tests`, … (~20 issues in
 * the backlog for what is one fault). Group by collection instead and keep the
 * slug as context, so the next outage is one issue with N events.
 */
export function groupCmsFetchFailures(event: ErrorEvent): ErrorEvent {
  const message = event.exception?.values?.[0]?.value ?? ""
  const match = CMS_FETCH_WRAPPER.exec(message)
  if (!match) return event
  const [, collection, slug] = match
  event.fingerprint = ["cms-fetch-failed", collection]
  event.extra = { ...event.extra, cmsCollection: collection, cmsSlug: slug }
  return event
}

/**
 * Browser-extension content scripts inject their own webpack bundle into our
 * page and throw from it — most often an unhandled promise rejection fired
 * after the extension's runtime context is torn down. `@sentry/nextjs`'s
 * rewriteFrames integration rewrites every frame's `filename`, stripping the
 * `chrome-extension://<id>` / `moz-extension://<id>` origin down to `app:///`.
 * By the time `beforeSend` runs the origin is already gone, so the
 * `chrome-extension://` `ignoreErrors` filter can never match these. None of
 * the bundle names below are marketing-site code — our client chunks live under
 * `_next/static/chunks/` — so dropping any event whose stack touches one removes
 * the noise without masking a real app error.
 *
 * Observed injectors:
 * - `injectedScript.bundle.js` — password managers, crypto wallets (MetaMask /
 *   Phantom): `Cannot read properties of undefined (reading 'sendMessage')`
 *   (Sentry #126283416).
 * - `content-main.js` — generic extension content-script entrypoint.
 * - `static/toolbar.js` — Vercel Toolbar extension: `n.target?.matches is not a
 *   function` (Sentry #128429098).
 * - `executors/<n>.js` — extension webpack chunk, property-mangled so the
 *   missing member reads as e.g. `M_ID`; `onunhandledrejection`
 *   (MARKETING-SITE-152). Seen on Edge, single-session bursts, 0 users impacted.
 */
const EXTENSION_INJECTED_BUNDLES: RegExp[] = [
  /injectedScript\.bundle\.js/,
  /content-main\.js/,
  /static\/toolbar\.js/,
  /(?:^|\/)executors\/[^/]+\.js/,
]

export function isExtensionInjectedFrame(event: ErrorEvent): boolean {
  const values = event.exception?.values
  if (!values) return false
  return values.some((value) =>
    value.stacktrace?.frames?.some((frame) =>
      EXTENSION_INJECTED_BUNDLES.some((re) => re.test(frame.filename ?? ""))
    )
  )
}

/**
 * The crawler/bot classes covered by Sentry's built-in inbound "filter known
 * crawlers/spiders" rule, reproduced here because the marketing-site Sentry
 * plan has no inbound filters configured — see the module-level fix
 * rationale in the 404-beacon / bot-noise change. Case-insensitive; matched
 * against a raw User-Agent string.
 */
const BOT_USER_AGENT_PATTERN =
  /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|HeadlessChrome|phantomjs|Lighthouse|Chrome-Lighthouse|Google Page Speed|GTmetrix|pingdom|uptimerobot|datadog|SemrushBot|AhrefsBot|python-requests|curl|wget|Googlebot|bingbot|Applebot|YandexBot|Baiduspider|Sogou|Bytespider/i

export function isBotUserAgent(ua: string): boolean {
  if (!ua) return false
  return BOT_USER_AGENT_PATTERN.test(ua)
}

/**
 * Server-side equivalent of the client `navigator.userAgent` check in
 * `instrumentation-client.ts` — reads the UA off the event's request
 * headers (case-insensitive key, matching how proxies/Next normalize
 * headers inconsistently) since there is no `navigator` on the server.
 */
export function isBotEvent(event: ErrorEvent): boolean {
  const headers = event.request?.headers
  if (!headers) return false
  const key = Object.keys(headers).find((k) => k.toLowerCase() === "user-agent")
  if (!key) return false
  const ua = headers[key]
  return typeof ua === "string" && isBotUserAgent(ua)
}
